import React from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import { withRouter } from 'react-router';
import MediaQuery from 'react-responsive';

import mea from '../../../services/mea';
import { PATH } from '../../../constants/path';
import {
    completeOnlineMeasWithAvailablePartners,
    completeOfflineMeasWithAvailablePartners,
    completeEbonMeasWithAvailablePartners,
    completeCategoryMeasWithAvailablePartners,
    completeMomentMeasWithAvailablePartners
} from '../../../services/mea/utils';
import { formatCategoryNameForUrl } from '../../../services/category/utils';

import CarouselComponent from '../../carousel/CarouselComponent';
import { LoadingComponent } from '../../loading/LoadingComponent';
import LinkButtonComponent from '../../button/LinkButtonComponent';
import ItemCarrouselComponent from '../../carousel/ItemCarrouselComponent';

import './MeaOfferSelection.scss';

const carouselOptions = {
    accessibility: false,
    dots: false,
    infinite: true,
    // Défilement animé très rapide, aligné sur les sections desktop
    speed: 150,
    autoplay: false,
    autoplaySpeed: 5000,
    slidesToShow: 1,
    slidesToScroll: 1,
    pauseOnFocus: true
};

const MobileMeaOfferSelection = ({
    isOnline,
    isOffline,
    isEbon,
    isMoment,
    category,
    i18nHome,
    partners,
    ebonPartners,
    ebonPartnersFetching,
    onlineOffers,
    offlineOffers,
    ebonOffers,
    categoryOffers,
    momentOffers
}) => {
    const getTitle = () => {
        if (isOnline) {
            return i18nHome.ONLINE_OFFERS_SELECTION_TITLE;
        } else if (isOffline) {
            return i18nHome.OFFLINE_OFFERS_SELECTION_TITLE;
        } else if (isEbon) {
            return i18nHome.EBON_OFFERS_SELECTION_TITLE;
        } else if (isMoment) {
            return i18nHome.MOMENT_OFFERS_SELECTION_TITLE;
        } else if (category) {
            return category.label;
        } else {
            return '';
        }
    };

    // Les sections mobiles sont toujours en carousel : complétion jusqu'à 8
    const targetCount = mea.constants.TARGET_CAROUSEL_OFFERS;

    const getData = () => {
        if (isOnline) {
            // Use auto-completion algorithm for online offers
            return completeOnlineMeasWithAvailablePartners(onlineOffers, partners, targetCount);
        } else if (isOffline) {
            // Use auto-completion algorithm for offline offers
            return completeOfflineMeasWithAvailablePartners(offlineOffers, partners, targetCount);
        } else if (isEbon) {
            // Use auto-completion algorithm for ebon offers
            return completeEbonMeasWithAvailablePartners(ebonOffers, ebonPartners, targetCount);
        } else if (isMoment) {
            // Use auto-completion algorithm for selection of the moment offers
            return completeMomentMeasWithAvailablePartners(
                momentOffers,
                partners,
                ebonPartners,
                targetCount
            );
        } else if (category) {
            // Use auto-completion algorithm for category offers
            return completeCategoryMeasWithAvailablePartners(
                categoryOffers.filter(m => Number(m.idRubrique) === Number(category.id)),
                partners,
                ebonPartners,
                Number(category.id),
                targetCount
            );
        } else {
            return [];
        }
    };

    const getPartner = mea => {
        if (isOnline || isOffline) {
            return partners[mea.idPartenaire] ? partners[mea.idPartenaire] : null;
        } else if (isEbon) {
            return ebonPartners.find(p => mea.idPartenaire === p.id)
                ? ebonPartners.find(p => mea.idPartenaire === p.id)
                : null;
        } else if (isMoment || category) {
            // Le marchand d'un MEA mixte se résout dans le référentiel
            // correspondant au type d'offre porté par le MEA.
            return mea.ebon
                ? ebonPartners.find(p => p.id === mea.idPartenaire) || null
                : partners[mea.idPartenaire] || null;
        } else {
            return null;
        }
    };

    // Compute the data once (includes the auto-completion algorithm) and gate
    // the whole section on the completed list, not on the raw MEA count. This
    // lets the section render even when no MEA is programmed, as long as the
    // completion algorithm can produce offers from available partners.
    const data = getData();
    const hasOffer = () => data.length > 0;

    return (
        hasOffer() && (
            <MediaQuery query="(max-width: 768px)">
                {matches =>
                    matches && (
                        <React.Fragment>
                            <div className="conteneur-mobile bloc-title">
                                <h2>{getTitle()}</h2>
                                {category && (
                                    <LinkButtonComponent
                                        color="third"
                                        text={i18nHome.CATEGORY_OFFERS_LINK}
                                        title={`${i18nHome.CATEGORY_OFFERS_LINK} : ${category.label}`}
                                        to={`${
                                            PATH.CASHBACK_CATEGORY_PATH
                                        }${formatCategoryNameForUrl(category.label)}-${
                                            category.id
                                        }`}
                                    />
                                )}
                            </div>
                            <section
                                className={`selection-carousel ${
                                    ebonPartnersFetching ? 'loading' : ''
                                }`}
                            >
                                {ebonPartnersFetching ? (
                                    <LoadingComponent />
                                ) : (
                                    <CarouselComponent options={carouselOptions}>
                                        {data.map((item, i) => {
                                            return (
                                                <ItemCarrouselComponent
                                                    item={item}
                                                    key={i}
                                                    partner={getPartner(item)}
                                                    isOffline={isOffline || item.offline}
                                                    isEbon={isEbon || item.ebon}
                                                    noPicture
                                                />
                                            );
                                        })}
                                    </CarouselComponent>
                                )}
                            </section>
                        </React.Fragment>
                    )
                }
            </MediaQuery>
        )
    );
};

MobileMeaOfferSelection.propTypes = {
    isOnline: PropTypes.bool,
    isOffline: PropTypes.bool,
    isEbon: PropTypes.bool,
    isMoment: PropTypes.bool,
    category: PropTypes.object
};

const mapStateToProps = state => {
    return {
        i18nHome: state.i18n.page.HOME,
        onlineOffers: mea.selectors.getMeasListByTypeId(state, mea.constants.ONLINE_OFFER),
        offlineOffers: mea.selectors.getMeasListByTypeId(state, mea.constants.OFFLINE_OFFER),
        ebonOffers: mea.selectors.getMeasListByTypeId(state, mea.constants.EBON_OFFER),
        categoryOffers: mea.selectors.getMeasListByTypeId(
            state,
            mea.constants.CATEGORY_OFFER_MEA_TYPE
        ),
        momentOffers: mea.selectors.getMeasListByTypeId(
            state,
            mea.constants.SELECTION_MOMENT_MEA_TYPE
        ),
        partners: state.partner.partners,
        ebonPartners: state.ebon.ebonPartners,
        ebonPartnersFetching: state.ebon.ebonPartnersFetching
    };
};

export default withRouter(connect(mapStateToProps)(MobileMeaOfferSelection));



// WEBPACK FOOTER //
// ./src/components/pages/home/MobileMeaOfferSelection.js